import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { QrReader } from 'react-qr-reader';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Button,
  InputAdornment,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Badge,
  Snackbar,
  Alert,
  Divider
} from '@mui/material';
import {
  Refresh,
  Search,
  Clear,
  FilterList,
  ExpandMore,
  ExpandLess,
  CalendarToday,
  LocationOn,
  Groups,
  ArrowBack,
  Close,
  CheckCircle
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import { isEventScannableToday, SCAN_DAYS_BEFORE } from '../utils/dateFormatter';
import i18n, { intlLocale } from '../i18n/i18n';
import './ScannerPage.css';

// For text set from callbacks whose dependencies should not change with the language.
const st = (key, options) => i18n.t(key, { ns: 'scanner', ...options });

const ScannerPage = ({ onScan }) => {
  const { t } = useTranslation('scanner');
  const [error, setError] = useState('');
  const [hasCamera, setHasCamera] = useState(true);
  const [cameraId, setCameraId] = useState(null);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [scannerKey, setScannerKey] = useState(Date.now());
  const [cameraWorking, setCameraWorking] = useState(false);
  const [isScanning, setIsScanning] = useState(true);
  // State for manual IBO number input
  const [iboNumber, setIboNumber] = useState('');
  const [iboError, setIboError] = useState('');
  // New state for event selection
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventSelector, setShowEventSelector] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventSearchQuery, setEventSearchQuery] = useState('');
  
  // Advanced filter states
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterEventType, setFilterEventType] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  
  // Collapsed sections state for event list
  const [collapsedSections, setCollapsedSections] = useState({});
  
  // Toast notification state
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  // Teams fetched from API
  const [availableTeamsFromAPI, setAvailableTeamsFromAPI] = useState([]);
  
  const scanTimeoutRef = useRef(null);
  const lastScannedRef = useRef(null);
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();

  // Reference to active media streams for cleanup
  const activeStreamRef = useRef(null);

  // Add a new state to control QrReader mounting
  const [showScanner, setShowScanner] = useState(true);

  // Track if events have been loaded
  const eventsLoadedRef = useRef(false);
  
  // Fetch teams from API
  const fetchTeams = useCallback(async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      
      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/teams`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          const teamsList = result.data || [];
          // Extract team names from the API response
          const teamNames = teamsList.map(team => team.name || team).filter(Boolean);
          setAvailableTeamsFromAPI(teamNames);
        }
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
      // Don't show error to user, just log it
    }
  }, [getAccessToken]);
  
  // Fetch events function
  const fetchEvents = useCallback(async (autoSelectSavedEvent = true) => {
    try {
      setEventsLoading(true);
      const token = await getAccessToken();
      if (!token) return;
      
      const response = await fetch(`${config.API_BASE_URL}/em/v1/events/upcoming`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          // DV-334: only events whose scanning is open today (from 2 days before until they end).
          const eventsList = (result.data || []).filter(event => isEventScannableToday(event));
          setEvents(eventsList);
          eventsLoadedRef.current = true;
          
          // Only auto-select saved event on initial load
          if (autoSelectSavedEvent) {
            // Try to load previously selected event from localStorage
            const savedEventId = localStorage.getItem('selectedEventId');
            if (savedEventId) {
              const savedEvent = eventsList.find(event => event.id.toString() === savedEventId);
              if (savedEvent) {
                setSelectedEvent(savedEvent);
                setShowEventSelector(false);
                // Cache in sessionStorage for quick restore on navigation
                sessionStorage.setItem('selectedEventData', JSON.stringify(savedEvent));
                return;
              }
            }
            
            // If no saved event found, show the selector
            setShowEventSelector(true);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(st('errors.loadEvents'));
    } finally {
      setEventsLoading(false);
    }
  }, [getAccessToken]);
  
  // Initialize on component mount - restore selected event or fetch events
  useEffect(() => {
    const initializeEvents = () => {
      const savedEventId = localStorage.getItem('selectedEventId');
      const savedEventDataStr = sessionStorage.getItem('selectedEventData');
      
      // Priority 1: Try to restore from sessionStorage (fastest, no API call needed)
      if (savedEventId && savedEventDataStr) {
        try {
          const savedEventData = JSON.parse(savedEventDataStr);
          // Verify the ID matches, and that the remembered event can still be scanned today: an
          // ended event would otherwise come back on its own and every scan would be refused.
          if (savedEventData.id.toString() === savedEventId && isEventScannableToday(savedEventData)) {
            setSelectedEvent(savedEventData);
            setShowEventSelector(false);
            // Restoring the selection is what saves the round trip, and it is worth keeping. The
            // list is a different question: it used to be replaced by this one event and marked
            // loaded, so every route into the selector that does not force a refresh offered a
            // choice of exactly the event you already had — "change event" with nothing to change
            // to. The full list is fetched behind the restored selection instead.
            if (events.length === 0) {
              fetchEvents(false);
            }
            return;
          }
        } catch (err) {
          console.error('Error parsing saved event data:', err);
          sessionStorage.removeItem('selectedEventData');
        }
      }
      
      // Priority 2: Check if we have events loaded in memory
      if (eventsLoadedRef.current && events.length > 0 && savedEventId) {
        // Try to restore the selected event from the loaded events
        const savedEvent = events.find(event => event.id.toString() === savedEventId);
        if (savedEvent && !selectedEvent) {
          setSelectedEvent(savedEvent);
          setShowEventSelector(false);
          return; // Don't fetch, we have the event
        }
      }
      
      // Priority 3: Fetch events only if we have no saved event or couldn't restore it
      if (!savedEventId || (savedEventId && !savedEventDataStr && events.length === 0)) {
        fetchEvents();
      }
    };
    
    initializeEvents();
    
    // Fetch teams separately (always needed for filters)
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  useEffect(() => {
    // Check for available cameras
    const getCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setAvailableCameras(videoDevices);
        
        if (videoDevices.length === 0) {
          setError(st('camera.errors.noCamera'));
          setHasCamera(false);
        } else {
          // By default, select the rear camera if available
          const rearCamera = videoDevices.find(device => 
            device.label.toLowerCase().includes('back') || 
            device.label.toLowerCase().includes('rear')
          );
          
          // Use rear camera or first available camera
          setCameraId(rearCamera?.deviceId || videoDevices[0].deviceId);
        }
      } catch (err) {
        console.error('Error enumerating devices:', err);
        setError(st('camera.errors.noAccess'));
      }
    };

    getCameras();
  }, []);

  // Try to manually access the camera if QrReader isn't showing it
  useEffect(() => {
    // This only runs if we have a camera ID and our video element exists
    if (cameraId && hasCamera) {
      setTimeout(() => {
        const videoElement = document.getElementById('qr-video');
        if (videoElement) {
          // Add a class to ensure visibility
          videoElement.classList.add('visible-video');
          
          // Check if video is actually playing/working
          if (videoElement.readyState >= 2) { // HAVE_CURRENT_DATA or better
            setCameraWorking(true);
            // Clear any error if camera is working
            setError('');
          }
        }
      }, 1000);
    }
  }, [cameraId, hasCamera, scannerKey]);

  // Cleanup function to stop the camera when component unmounts or navigates away
  useEffect(() => {
    // Function to stop all tracks in a stream
    const stopMediaStream = (stream) => {
      if (!stream) return;
      
      stream.getTracks().forEach(track => {
        track.stop();
      });
    };
    
    // Return cleanup function
    return () => {
      console.log('Stopping camera on unmount/navigation');
      
      // Stop any active stream in the ref
      if (activeStreamRef.current) {
        stopMediaStream(activeStreamRef.current);
        activeStreamRef.current = null;
      }
      
      // Stop any stream from video element
      if (videoRef.current && videoRef.current.srcObject) {
        stopMediaStream(videoRef.current.srcObject);
        videoRef.current.srcObject = null;
      }
      
      // Get all video elements in the component and stop their streams
      const videoElements = document.querySelectorAll('#qr-container video');
      videoElements.forEach(video => {
        if (video.srcObject) {
          stopMediaStream(video.srcObject);
          video.srcObject = null;
        }
      });
    };
  }, []);

  const handleScan = (data) => {
    if (!isScanning || !selectedEvent) return;
    
    if (!data || data === lastScannedRef.current) return;
    
    setCameraWorking(true);
    setError('');
    
    console.log('QR Code scanned:', data);
    lastScannedRef.current = data;
    
    setIsScanning(false);
    
    // Store the active stream before navigation
    const videoElement = document.getElementById('qr-video');
    if (videoElement && videoElement.srcObject) {
      activeStreamRef.current = videoElement.srcObject;
    }
    
    // Pass both the QR data and the selected event ID
    onScan(data, selectedEvent.id);
    navigate('/result');
    
    scanTimeoutRef.current = setTimeout(() => {
      setIsScanning(true);
      lastScannedRef.current = null;
    }, 5000);
  };

  // Handle manual IBO submission
  const handleManualSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedEvent) {
      setError(t('manual.errors.selectEventFirst'));
      setShowEventSelector(true);
      return;
    }
    
    setIboError('');
    
    // Validate the IBO input
    if (!iboNumber.trim()) {
      setIboError(t('manual.errors.required'));
      return;
    }
    
    if (!/^\d+$/.test(iboNumber)) {
      setIboError(t('manual.errors.digitsOnly'));
      return;
    }
    
    // Process the manual entry
    console.log("Manual IBO number entered:", iboNumber);
    
    // Call the parent component's handler with event ID
    if (onScan) {
      onScan(iboNumber, selectedEvent.id);
    }
    
    // Navigate to result page
    navigate('/result');
  };

  useEffect(() => {
    return () => {
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
    };
  }, []);

  // react-qr-reader@3.0.0-beta-1 captures onResult ONCE, in a useEffect with empty
  // deps, so every later render's callback is ignored. Our callback closes over
  // handleScan, which closes over selectedEvent — so the camera kept reporting the
  // event that was selected when it started. Changing the event re-rendered this
  // component but never remounted the reader, and a ticket for the newly chosen
  // event was validated against the old one.
  //
  // The callback handed to the reader must therefore be free of state. It reads the
  // handlers through refs, which are reassigned on every render, so the frozen
  // closure still reaches the current ones.
  const handleScanRef = useRef(null);
  const handleErrorRef = useRef(null);

  const handleError = (err) => {
    // Define common detection errors that should be completely ignored (not even logged)
    const silentErrors = [
      'selectBestPatterns',
      'NotFoundException',
      'No QR code found',
      'Failed to find pattern'
    ];
    
    // Check if this is a silent error that should be completely ignored
    if (err && (
      silentErrors.some(msg => err.message?.includes(msg) || err.toString().includes(msg)) ||
      err.name === 'NotFoundException' ||
      (typeof err === 'object' && Object.keys(err).length === 0) ||
      err === 'e' // Handle the specific "e" error
    )) {
      // Completely silent - don't even log to console
      return;
    }
    
    // At this point, it's a non-detection error worth logging
    // console.error('QR Scanner Error:', err);
    
    // If camera is known to be working, ignore most errors
    if (cameraWorking) {
      console.log('Ignoring error because camera is known to be working');
      return;
    }
    
    // More specific error messages based on the error type
    if (err.name === 'NotAllowedError') {
      setError(t('camera.errors.denied'));
    } else if (err.name === 'NotFoundError') {
      setError(t('camera.errors.notFound'));
    } else if (err.name === 'NotReadableError') {
      setError(t('camera.errors.inUse'));
    } else {
      // Only set this error if we haven't already confirmed the camera is working
      const videoElement = document.getElementById('qr-video');
      if (videoElement && videoElement.readyState >= 2) {
        // Camera is working despite the error, so don't show an error message
        setCameraWorking(true);
        setError('');
      } else {
        setError(t('camera.errors.generic', { detail: err.message || t('camera.errors.unknown') }));
      }
    }
  };

  const changeCamera = (deviceId) => {
    setCameraId(deviceId);
    setError('');
    setCameraWorking(false); // Reset camera working status when changing cameras
    setScannerKey(Date.now());
  };

  const retryCamera = () => {
    setError('');
    setCameraWorking(false); // Reset camera working status when retrying
    setScannerKey(Date.now());
  };

  handleScanRef.current = handleScan;
  handleErrorRef.current = handleError;

  // Handle event selection (memoized)
  const handleEventSelect = useCallback((event) => {
    setSelectedEvent(event);
    setShowEventSelector(false);
    
    localStorage.setItem('selectedEventId', event.id.toString());
    sessionStorage.setItem('selectedEventData', JSON.stringify(event));
    
    setToastMessage(st('toast.selected', { name: event.name }));
    setToastOpen(true);
  }, []);
  
  // Handle opening event selector and optionally refresh events
  const handleOpenEventSelector = useCallback((shouldRefresh = false) => {
    setShowEventSelector(true);
    if (shouldRefresh) {
      // Clear session cache to force fresh data
      sessionStorage.removeItem('selectedEventData');
      fetchEvents(false); // Don't auto-close modal when manually refreshing
      fetchTeams(); // Also refresh teams
    }
  }, [fetchEvents, fetchTeams]);
  
  // Handle search query change (memoized)
  const handleSearchChange = useCallback((e) => {
    setEventSearchQuery(e.target.value);
  }, []);
  
  // Handle clear search (memoized)
  const handleClearSearch = useCallback(() => {
    setEventSearchQuery('');
  }, []);
  
  // Handle clear all filters
  const handleClearAllFilters = useCallback(() => {
    setEventSearchQuery('');
    setFilterTeam('all');
    setFilterEventType('all');
    setFilterDateFrom('');
    setFilterDateTo('');
  }, []);

  // Updated handleBack function with a two-phase approach
  const handleBack = () => {
    // First phase: Unmount all camera components and cancel all streams
    setShowScanner(false); // This will unmount the QrReader component
    setIsScanning(false);
    
    // Explicitly stop all camera streams
    const stopAllCameraStreams = () => {
      // MediaStream tracks
      const allTracks = [];
      
      // 1. First collect all active media tracks we can find
      if (activeStreamRef.current) {
        activeStreamRef.current.getTracks().forEach(track => allTracks.push(track));
        activeStreamRef.current = null;
      }
      
      // 2. Check the video ref
      if (videoRef.current && videoRef.current.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(track => allTracks.push(track));
        videoRef.current.srcObject = null;
      }
      
      // 3. Find all video elements on the page
      document.querySelectorAll('video').forEach(video => {
        if (video && video.srcObject) {
          video.srcObject.getTracks().forEach(track => allTracks.push(track));
          video.srcObject = null;
          video.pause();
        }
      });
      
      // 4. Stop all collected tracks
      console.log(`Stopping ${allTracks.length} media tracks`);
      allTracks.forEach(track => {
        try {
          track.stop();
          console.log(`Stopped ${track.kind} track`);
        } catch (e) {
          console.error('Error stopping track:', e);
        }
      });
      
      // 5. Extra aggressive approach - force browser to reset media devices
      if (navigator.mediaDevices) {
        if (typeof navigator.mediaDevices.getUserMedia === 'function') {
          navigator.mediaDevices.getUserMedia({ audio: false, video: false })
            .catch(() => {});
        }
        
        // Some browsers have a new feature to explicitly stop capture devices
        if (typeof navigator.mediaDevices.stopUserMediaCapture === 'function') {
          try {
            navigator.mediaDevices.stopUserMediaCapture();
          } catch (e) {}
        }
      }
    };
    
    // Execute the cleanup
    stopAllCameraStreams();
    
    // Wait a short time to ensure browser has time to release the camera
    // before navigating away
    setTimeout(() => {
      navigate('/');
    }, 300); // Brief delay to allow browser to process stream cleanup
  };

  // Use teams fetched from API instead of deriving from events
  const availableTeams = useMemo(() => {
    return availableTeamsFromAPI.sort();
  }, [availableTeamsFromAPI]);
  
  const availableEventTypes = useMemo(() => {
    const typeSet = new Set();
    events.forEach(event => {
      if (event.eventType) {
        typeSet.add(event.eventType);
      }
    });
    return Array.from(typeSet).sort();
  }, [events]);
  
  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (eventSearchQuery.trim()) count++;
    if (filterTeam !== 'all') count++;
    if (filterEventType !== 'all') count++;
    if (filterDateFrom) count++;
    if (filterDateTo) count++;
    return count;
  }, [eventSearchQuery, filterTeam, filterEventType, filterDateFrom, filterDateTo]);
  
  // Filter and sort events - current month first, then apply all filters (memoized)
  const filteredAndSortedEvents = useMemo(() => {
    let filteredEvents = [...events];
    
    // Apply search filter
    if (eventSearchQuery.trim()) {
      const query = eventSearchQuery.toLowerCase();
      filteredEvents = filteredEvents.filter(event => 
        event.name.toLowerCase().includes(query) ||
        (event.location && event.location.toLowerCase().includes(query)) ||
        (event.description && event.description.toLowerCase().includes(query))
      );
    }
    
    // Apply team filter
    if (filterTeam !== 'all') {
      filteredEvents = filteredEvents.filter(event => 
        event.eligibleTeams && 
        (event.eligibleTeams.includes('all') || event.eligibleTeams.includes(filterTeam))
      );
    }
    
    // Apply event type filter
    if (filterEventType !== 'all') {
      filteredEvents = filteredEvents.filter(event => 
        event.eventType === filterEventType
      );
    }
    
    // Apply date from filter
    if (filterDateFrom) {
      const fromDate = new Date(filterDateFrom);
      filteredEvents = filteredEvents.filter(event => {
        const eventDate = new Date(event.startDate);
        return eventDate >= fromDate;
      });
    }
    
    // Apply date to filter
    if (filterDateTo) {
      const toDate = new Date(filterDateTo);
      toDate.setHours(23, 59, 59, 999); // Include the entire end date
      filteredEvents = filteredEvents.filter(event => {
        const eventDate = new Date(event.startDate);
        return eventDate <= toDate;
      });
    }
    
    // Sort by current month first, then chronologically
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    return filteredEvents.sort((a, b) => {
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      
      const isACurrentMonth = dateA.getMonth() === currentMonth && dateA.getFullYear() === currentYear;
      const isBCurrentMonth = dateB.getMonth() === currentMonth && dateB.getFullYear() === currentYear;
      
      // Prioritize current month events
      if (isACurrentMonth && !isBCurrentMonth) return -1;
      if (!isACurrentMonth && isBCurrentMonth) return 1;
      
      // Otherwise sort chronologically
      return dateA - dateB;
    });
  }, [events, eventSearchQuery, filterTeam, filterEventType, filterDateFrom, filterDateTo]);

  // The event type as shown: known types are translated, any other is shown as the server sent it.
  const eventTypeLabel = (type) => (type ? t(`eventTypes.${type}`, { defaultValue: type }) : t('eventTypes.default'));

  const eventTypeBgColor = (type) => {
    switch ((type || '').toUpperCase()) {
      case 'CONFERENCE': return { bg: '#e3f2fd', color: '#1565c0' };
      case 'SEMINAR': return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'OPEN': return { bg: '#f3e5f5', color: '#7b1fa2' };
      case 'WORKSHOP': return { bg: '#fff3e0', color: '#e65100' };
      default: return { bg: '#f5f5f5', color: '#616161' };
    }
  };

  const sectionHeaderStyle = (section) => {
    if (section.isTodaySection) return { background: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)', borderLeft: '4px solid #4caf50' };
    if (section.isCurrentMonth) return { background: 'linear-gradient(135deg, #e3f2fd, #bbdefb)', borderLeft: '4px solid #2196f3' };
    if (section.isPast) return { background: '#f5f5f5', borderLeft: '4px solid #bdbdbd' };
    return { background: '#fafafa', borderLeft: '4px solid #9e9e9e' };
  };

  // Event selector modal component
  // Rendered as a function, not as <EventSelectorModal />, and deliberately so.
  //
  // Declaring a component inside another component gives React a new component type on every
  // render, so the whole dialog was unmounted and remounted whenever ScannerPage re-rendered. The
  // search field is ScannerPage state, so every keystroke re-rendered the parent, remounted the
  // dialog and threw away the focus and the text — which made choosing a different event a fight.
  // Calling it keeps the JSX in ScannerPage's own tree. It holds no hooks, so there is nothing to
  // move.
  const renderEventSelector = () => (
    <Dialog 
      open={showEventSelector} 
      fullWidth 
      maxWidth="sm"
      PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        {/* DialogTitle already renders an h2; a nested h6 is invalid markup. */}
        <Typography variant="h6" component="span" fontWeight={700}>{t('selector.title')}</Typography>
        <Stack direction="row" spacing={0.5}>
          <IconButton
            onClick={() => {
              sessionStorage.removeItem('selectedEventData');
              fetchEvents(false);
              fetchTeams();
            }}
            disabled={eventsLoading}
            size="small"
            title={t('selector.refresh')}
          >
            <Refresh />
          </IconButton>
          {selectedEvent && (
            <IconButton
              onClick={() => setShowEventSelector(false)}
              size="small"
              title={t('selector.close')}
            >
              <Close />
            </IconButton>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 2, pb: 2 }}>
        {events.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('selector.searchPlaceholder')}
              value={eventSearchQuery}
              onChange={handleSearchChange}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                ),
                endAdornment: eventSearchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}><Clear fontSize="small" /></IconButton>
                  </InputAdornment>
                ) : null
              }}
              sx={{ mb: 1.5 }}
            />

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Button
                size="small"
                variant={showAdvancedFilters ? 'contained' : 'outlined'}
                startIcon={<FilterList />}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                <Badge badgeContent={activeFiltersCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}>
                  {t('selector.filters')}
                </Badge>
              </Button>
              {activeFiltersCount > 0 && (
                <Button size="small" color="error" onClick={handleClearAllFilters} sx={{ textTransform: 'none' }}>
                  {t('selector.clearAll')}
                </Button>
              )}
            </Stack>

            <Collapse in={showAdvancedFilters}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 1 }}>
                <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t('selector.team')}</InputLabel>
                    <Select value={filterTeam} label={t('selector.team')} onChange={(e) => setFilterTeam(e.target.value)}>
                      <MenuItem value="all">{t('selector.allTeams')}</MenuItem>
                      {availableTeams.map(team => <MenuItem key={team} value={team}>{team}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t('selector.eventType')}</InputLabel>
                    <Select value={filterEventType} label={t('selector.eventType')} onChange={(e) => setFilterEventType(e.target.value)}>
                      <MenuItem value="all">{t('selector.allTypes')}</MenuItem>
                      {availableEventTypes.map(type => <MenuItem key={type} value={type}>{eventTypeLabel(type)}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    size="small" fullWidth type="date" label={t('selector.fromDate')}
                    value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    size="small" fullWidth type="date" label={t('selector.toDate')}
                    value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
              </Paper>
            </Collapse>
          </Box>
        )}

        {eventsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filteredAndSortedEvents.length > 0 ? (
          <>
            {activeFiltersCount > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                {t('selector.showing', { shown: filteredAndSortedEvents.length, count: events.length })}
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {groupedEventSections.map(section => {
                const isCollapsed = collapsedSections[section.key] !== undefined
                  ? collapsedSections[section.key]
                  : !section.defaultExpanded;

                return (
                  <Paper key={section.key} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <ListItemButton
                      onClick={() => toggleSection(section.key)}
                      sx={{ ...sectionHeaderStyle(section), py: 1, px: 2 }}
                    >
                      {isCollapsed ? <ExpandMore fontSize="small" sx={{ mr: 1 }} /> : <ExpandLess fontSize="small" sx={{ mr: 1 }} />}
                      <ListItemText
                        primary={section.label}
                        primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                      />
                      <Chip
                        label={`${section.events.length}`}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: 'rgba(0,0,0,0.06)' }}
                      />
                    </ListItemButton>

                    <Collapse in={!isCollapsed}>
                      <List disablePadding>
                        {section.events.map((event, idx) => {
                          const typeColors = eventTypeBgColor(event.eventType);
                          return (
                            <React.Fragment key={event.id}>
                              {idx > 0 && <Divider />}
                              <ListItemButton
                                onClick={() => handleEventSelect(event)}
                                sx={{
                                  py: 1.5, px: 2,
                                  ...(idx === 0 && { borderTop: '1px solid', borderColor: 'divider' }),
                                  ...(isEventToday(event) && { borderLeft: '3px solid #4caf50', bgcolor: 'rgba(76,175,80,0.04)' }),
                                  '&:hover': { bgcolor: 'action.hover' }
                                }}
                              >
                              <Box sx={{ width: '100%' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                  <Typography variant="body2" fontWeight={600} sx={{ flex: 1, lineHeight: 1.3 }}>
                                    {event.name}
                                  </Typography>
                                  <Chip
                                    label={eventTypeLabel(event.eventType)}
                                    size="small"
                                    sx={{
                                      height: 20, fontSize: '0.65rem', fontWeight: 700,
                                      letterSpacing: 0.3,
                                      bgcolor: typeColors.bg, color: typeColors.color
                                    }}
                                  />
                                </Stack>

                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.3 }}>
                                  <CalendarToday sx={{ fontSize: 13, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(event.startDate) === formatDate(event.endDate)
                                      ? `${formatDate(event.startDate)} \u00B7 ${formatTime(event.startDate)} – ${formatTime(event.endDate)}`
                                      : `${formatDate(event.startDate)}, ${formatTime(event.startDate)} – ${formatDate(event.endDate)}, ${formatTime(event.endDate)}`
                                    }
                                  </Typography>
                                </Stack>

                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.3 }}>
                                  <LocationOn sx={{ fontSize: 13, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {event.location || t('selector.noLocation')}
                                  </Typography>
                                </Stack>

                                {event.eligibleTeams && event.eligibleTeams.length > 0 && (
                                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                                    <Groups sx={{ fontSize: 13, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                      {t('selector.teams', { count: event.eligibleTeams.length })}
                                    </Typography>
                                    {event.eligibleTeams.map((team, i) => (
                                      <Chip
                                        key={i}
                                        label={team}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 18, fontSize: '0.65rem', fontWeight: 500, borderColor: '#d1c4e9', color: '#5e35b1' }}
                                      />
                                    ))}
                                  </Stack>
                                )}
                              </Box>
                            </ListItemButton>
                            </React.Fragment>
                          );
                        })}
                      </List>
                    </Collapse>
                  </Paper>
                );
              })}
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            {activeFiltersCount > 0 ? (
              <>
                <Typography variant="body1" gutterBottom>{t('selector.noMatches')}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  {eventSearchQuery && t('selector.summary.search', { query: eventSearchQuery })}
                  {filterTeam !== 'all' && ` \u2022 ${t('selector.summary.team', { team: filterTeam })}`}
                  {filterEventType !== 'all' && ` \u2022 ${t('selector.summary.type', { type: eventTypeLabel(filterEventType) })}`}
                  {filterDateFrom && ` \u2022 ${t('selector.summary.from', { date: new Date(filterDateFrom).toLocaleDateString(intlLocale()) })}`}
                  {filterDateTo && ` \u2022 ${t('selector.summary.to', { date: new Date(filterDateTo).toLocaleDateString(intlLocale()) })}`}
                </Typography>
                <Button variant="outlined" onClick={handleClearAllFilters} sx={{ textTransform: 'none' }}>
                  {t('selector.clearAllFilters')}
                </Button>
              </>
            ) : (
              <>
                <Typography variant="body1" gutterBottom>{t('selector.empty.title')}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  {t('selector.empty.hint', { count: SCAN_DAYS_BEFORE })}
                </Typography>
                <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBack} sx={{ textTransform: 'none' }}>
                  {t('selector.backToDashboard')}
                </Button>
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );

  // Helper function to format date string to readable date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(intlLocale(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(intlLocale(), {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  
  const formatMonthYear = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(intlLocale(), { month: 'long', year: 'numeric' });
  };
  
  const isEventToday = useCallback((event) => {
    if (!event || !event.startDate || !event.endDate) return false;
    const timezone = event.timezone || 'America/Los_Angeles';
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    const now = new Date();
    // 'en-US' on purpose here and below: the text is never shown, it is split as M/D/YYYY.
    const todayStr = now.toLocaleDateString('en-US', { timeZone: timezone });
    const startStr = startDate.toLocaleDateString('en-US', { timeZone: timezone });
    const endStr = endDate.toLocaleDateString('en-US', { timeZone: timezone });
    const toDateParts = (str) => { const [m, d, y] = str.split('/').map(Number); return new Date(y, m - 1, d); };
    const todayDate = toDateParts(todayStr);
    const startLocalDate = toDateParts(startStr);
    const endLocalDate = toDateParts(endStr);
    return todayDate >= startLocalDate && todayDate <= endLocalDate;
  }, []);
  
  const toggleSection = useCallback((sectionKey) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  }, []);
  
  // Group filtered events into sections: Today (ongoing), by month, and past
  // Uses endDate as the reference: event is "today/ongoing" if endDate hasn't passed,
  // "past" if endDate has passed, otherwise grouped by startDate month
  const groupedEventSections = useMemo(() => {
    const now = new Date();
    const currentMonthKey = formatMonthYear(now.toISOString());
    
    const todayEvents = [];
    const futureByMonth = {};
    const pastEvents = [];
    
    const toDateParts = (str) => { const [m, d, y] = str.split('/').map(Number); return new Date(y, m - 1, d); };
    
    filteredAndSortedEvents.forEach(event => {
      const timezone = event.timezone || 'America/Los_Angeles';
      const endDate = new Date(event.endDate || event.startDate);
      const startDate = new Date(event.startDate);
      
      const todayStr = now.toLocaleDateString('en-US', { timeZone: timezone });
      const startStr = startDate.toLocaleDateString('en-US', { timeZone: timezone });
      const endStr = endDate.toLocaleDateString('en-US', { timeZone: timezone });
      
      const todayLocal = toDateParts(todayStr);
      const startLocal = toDateParts(startStr);
      const endLocal = toDateParts(endStr);
      
      if (todayLocal >= startLocal && todayLocal <= endLocal) {
        todayEvents.push(event);
      } else if (endLocal < todayLocal) {
        pastEvents.push(event);
      } else {
        const monthKey = formatMonthYear(event.startDate);
        if (!futureByMonth[monthKey]) {
          futureByMonth[monthKey] = { monthKey, date: startDate, events: [] };
        }
        futureByMonth[monthKey].events.push(event);
      }
    });
    
    const sortedMonths = Object.values(futureByMonth).sort((a, b) => a.date - b.date);
    
    const sections = [];
    
    if (todayEvents.length > 0) {
      sections.push({ key: 'today', label: t('selector.sections.today'), events: todayEvents, defaultExpanded: true, isTodaySection: true });
    }
    
    sortedMonths.forEach(group => {
      const isCurrentMonth = group.monthKey === currentMonthKey;
      // Spanish month names are lower case ("noviembre de 2026"); a heading starts with a capital.
      sections.push({ key: group.monthKey, label: group.monthKey.charAt(0).toUpperCase() + group.monthKey.slice(1), events: group.events, defaultExpanded: isCurrentMonth, isCurrentMonth });
    });
    
    if (pastEvents.length > 0) {
      sections.push({ key: 'past', label: t('selector.sections.past'), events: pastEvents.sort((a, b) => new Date(b.startDate) - new Date(a.startDate)), defaultExpanded: false, isPast: true });
    }
    
    return sections;
    // t changes with the language, and so do the month headings and section labels built here.
  }, [filteredAndSortedEvents, t]);

  return (
    <div className="scanner-container">
      {renderEventSelector()}
      
      <h2>{t('title')}</h2>
      
      {selectedEvent && (
        <div className="selected-event-display">
          <h3>{t('selectedEvent', { name: selectedEvent.name })}</h3>
          <button 
            className="change-event-button"
            onClick={() => handleOpenEventSelector(true)}
          >
            {t('changeEvent')}
          </button>
        </div>
      )}
      
      {error && <div className="error-message">{error}</div>}
      
      {/* Camera selector dropdown (if multiple cameras detected) */}
      {availableCameras.length > 1 && (
        <div className="camera-selector">
          <label htmlFor="camera-select">{t('camera.selectLabel')}</label>
          <select 
            id="camera-select"
            className="camera-select"
            value={cameraId || ''}
            onChange={(e) => {
              setCameraId(e.target.value);
              setCameraWorking(false);
              setScannerKey(Date.now());
            }}
          >
            {availableCameras.map((camera, index) => (
              <option key={camera.deviceId} value={camera.deviceId}>
                {camera.label || t('camera.option', { number: index + 1 })}
              </option>
            ))}
          </select>
        </div>
      )}
      
      {/* QR Code Scanner */}
      <div className="qr-reader-container" id="qr-container">
        {hasCamera && isScanning && showScanner && selectedEvent && (
          <>
            {/* Add a direct video element as fallback */}
            <video 
              ref={videoRef}
              id="fallback-video" 
              className="fallback-video"
              autoPlay 
              playsInline 
              muted
              onPlaying={() => {
                setCameraWorking(true);
                setError('');
                
                // Store the stream for later cleanup
                if (videoRef.current && videoRef.current.srcObject) {
                  activeStreamRef.current = videoRef.current.srcObject;
                }
              }}
            />
            
            <QrReader
              key={scannerKey}
              constraints={{
                ...cameraId ? { deviceId: { exact: cameraId } } : { facingMode: 'environment' },
                width: { ideal: 1280 },
                height: { ideal: 720 }
              }}
              onResult={(result, error) => {
                // No state here — see the refs above. handleScan re-checks
                // isScanning itself, with the current value rather than the
                // one this closure was born with.
                if (result) {
                  handleScanRef.current?.(result?.text);
                }
                if (error) {
                  handleErrorRef.current?.(error);
                }
              }}
              className="qr-reader"
              scanDelay={300}
              videoId="qr-video"
              videoStyle={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 1
              }}
            />
          </>
        )}
        
        <div className="scanner-overlay">
          <div className="scanner-target"></div>
        </div>
      </div>
      
      <button onClick={retryCamera} className="retry-button">
        {t('camera.refresh')}
      </button>
      
      <p className="scanner-instructions">
        {t('camera.instructions')}
      </p>
      
      {/* Manual IBO Entry Section (moved below the scanner) */}
      <div className="section-divider">
        <span>{t('manual.or')}</span>
      </div>
      
      <div className="manual-input-container">
        <h3>{t('manual.heading')}</h3>
        <form onSubmit={handleManualSubmit} className="manual-input-form">
          <div className="input-group">
            <input
              type="text"
              className={`manual-input ${iboError ? 'error' : ''}`}
              placeholder={t('manual.placeholder')}
              value={iboNumber}
              onChange={(e) => setIboNumber(e.target.value)}
            />
            {iboError && <div className="input-error">{iboError}</div>}
          </div>
          <button type="submit" className="validate-button">
            {t('manual.submit')}
          </button>
        </form>
      </div>
      
      <button onClick={handleBack} className="back-button">
        {t('backToMenu')}
      </button>

      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%', fontWeight: 600 }}
          icon={<CheckCircle fontSize="inherit" />}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ScannerPage; 